import * as XLSX from 'xlsx';
import {
  Document, Packer, Paragraph, HeadingLevel, TextRun, Table, TableRow, TableCell,
  WidthType, AlignmentType, BorderStyle, PageNumber, Header, Footer,
} from 'docx';
import { fmtDate, fmtInstantDate, todayInputValue, parseLocalDate, daysFromToday } from '@/lib/dates';
import type { TrainingProgressForUser } from '@/lib/apiClient';
import { TRAINING } from '../training/content';
import { suggestedTrack, type TrainingViewer } from '../training/lib/access';
import { targetTopics, topicExists } from '../training/lib/target';

/**
 * The shape of the Training progress roll-up, and the four files it exports.
 *
 * ⚠️ ONE ROW MODEL, FOUR RENDERERS. The table, the two workbooks and the two
 * documents all read `buildRows`. They were nearly written as separate passes
 * over the API payload, which is how the page and the export start disagreeing
 * about a denominator — the one number here that is genuinely hard (see below).
 *
 * ⚠️ THE DENOMINATOR IS PER PERSON, AND IT IS ONE TRACK. The guide is
 * access-filtered, so "12 of 40" is meaningless unless the 40 is the topics THAT
 * PERSON is expected to complete. This used to be every track they could OPEN,
 * which sounded like the same thing and is not: only the Administrator track is
 * access-gated, so a PM was being measured against the estimator, PE, ops and
 * field leader material too and topped out at 59% having done everything asked
 * of them. Nobody but an admin who read all six could reach 100%, which made
 * the "complete" state unreachable — for a roll-up whose whole job is to say
 * who is finished. The denominator is now foundations plus the ONE track
 * `suggestedTrack` puts them on, the same track the guide badges as theirs.
 * Every export says which track that was, because a spreadsheet leaves the room
 * and the explanation has to travel with it.
 *
 * ⚠️ TWO AUDIENCES, DELIBERATELY DIFFERENT. The manager report names people and
 * calls out who has not started; the team summary carries no names at all, only
 * role-level counts. Sharing a company-wide file with names against 0% is not
 * the same act as an admin opening this tab, so the neutral version is not just
 * the detailed one with a column hidden — it never builds the names.
 */

// ─── Row model ────────────────────────────────────────────────────────────────

export type TrainingState = 'complete' | 'started' | 'not-started';

export interface TrainingNote {
  topicId: string;
  title: string;
  note: string;
  completedAt: string | null;
}

export interface TrainingRow {
  id: string;
  name: string;
  email: string | null;
  roles: string[];
  rolesText: string;
  done: number;
  total: number;
  /** 0–100, rounded. 0 when the person has no reachable topics at all. */
  pct: number;
  /** Ticks recorded against an older guide revision — done, but possibly stale. */
  stale: number;
  /** The track the denominator was measured against — "74%" is not answerable
   *  without it. 'Foundations only' when no track is suggested for them. */
  trackLabel: string;
  notes: TrainingNote[];
  last: string | null;
  state: TrainingState;
}

export interface RoleTally {
  role: string;
  people: number;
  complete: number;
  notStarted: number;
  /** Topics ticked ÷ topics targeted across everyone holding this role. */
  pct: number;
}

export interface TrainingSummary {
  people: number;
  complete: number;
  started: number;
  notStarted: number;
  topicsDone: number;
  topicsTarget: number;
  /** Company-wide completion — topics ticked ÷ topics targeted, not a mean of means. */
  pct: number;
  byRole: RoleTally[];
}

/** Title of a topic, for the notes appendix. Falls back to the slug. */
export function topicTitle(topicId: string): string {
  for (const s of TRAINING.foundations) for (const t of s.topics) if (t.id === topicId) return t.title;
  for (const tr of TRAINING.tracks) for (const s of tr.sections) for (const t of s.topics) if (t.id === topicId) return t.title;
  for (const m of TRAINING.modules) for (const t of m.topics) if (t.id === topicId) return t.title;
  return topicId; // A topic since deleted — show the slug rather than nothing.
}

export function buildRows(data: TrainingProgressForUser[]): TrainingRow[] {
  return data.map((u): TrainingRow => {
    // costsEnabled: true so the Financials topics are in everyone's target.
    // The toggle is company-wide — including it would move every denominator
    // at once and say nothing about the person.
    //
    // hasActiveAssignments: true for a related but distinct reason. This one
    // DOES vary per person, but it varies WEEK TO WEEK — somebody between jobs
    // has none — so reading it here would move one person's denominator on
    // transient data and make the roll-up jitter for reasons that are not about
    // their training. `TrainingProgressForUser` does not carry assignments
    // anyway, so true is also the only figure available that is not a guess.
    const viewer: TrainingViewer = {
      roles: u.roles, permissions: u.permissions, canViewAll: u.canViewAll, costsEnabled: true,
      hasActiveAssignments: true,
    };
    // ONE track, not every track they could open — see the note at the top.
    // `suggestedTrack` is the same resolver the guide uses for the "your role"
    // badge, so the roll-up measures people against the track they were told to
    // read. Nobody is suggested every track, and someone whose roles match none
    // of them falls back to foundations, which is genuinely all we know they owe.
    const track = suggestedTrack(viewer, TRAINING.tracks);
    const target = targetTopics(viewer, track?.id);
    const counted = u.topics.filter((t) => target.has(t.topicId));
    const done = counted.filter((t) => t.completedAt != null);
    const total = target.size;
    // "Has ticked nothing at all" is a different question from "has none of the
    // topics we count", and `not-started` is the first. Someone carrying old
    // appendix ticks — recordable until the tick was taken off those cards —
    // has plainly started, and calling them not-started would put them on the
    // chase list.
    const tickedAnything = u.topics.some((t) => t.completedAt != null && topicExists(t.topicId));

    return {
      id: u.userId,
      name: u.displayName ?? u.email ?? '—',
      email: u.email,
      roles: u.roles,
      rolesText: u.roles.join(', ') || '—',
      done: done.length,
      total,
      pct: total > 0 ? Math.round((done.length / total) * 100) : 0,
      stale: done.filter((t) => t.guideRevision !== TRAINING.revision).length,
      trackLabel: track?.label ?? 'Foundations only',
      // ⚠️ NOTES ARE NOT SCOPED TO THE TARGET. Someone can annotate an appendix
      // card or another role's track, and those notes are still theirs and still
      // addressed to their manager — filtering them by the denominator would
      // have deleted them from this view the moment the denominator narrowed.
      // Topics that no longer EXIST are dropped, which is the check that was
      // actually wanted here.
      notes: u.topics.filter((t) => t.note && topicExists(t.topicId)).map((t) => ({
        topicId: t.topicId, title: topicTitle(t.topicId), note: t.note!, completedAt: t.completedAt,
      })),
      last: u.lastActivity,
      // ⚠️ MEASURED IN TICKS, AND ONLY TICKS. Someone can save a note without
      // ticking the topic (TopicProgress allows it), so a `not-started` row is
      // "has ticked nothing" — NOT "has never opened the guide". Every label
      // downstream has to say the former; the stronger claim is false for
      // anyone who read, annotated, and moved on without ticking. Folding notes
      // into `started` was the alternative, and it is worse: it would put a 0%
      // bar under "in progress" and make the count disagree with the
      // percentage it sits next to.
      state: !tickedAnything ? 'not-started' : (total > 0 && done.length >= total ? 'complete' : 'started'),
    };
  }).sort(byProgressThenName);
}

/** Furthest along first, then alphabetically — the page's default order. */
export function byProgressThenName(a: TrainingRow, b: TrainingRow): number {
  return (b.done / (b.total || 1)) - (a.done / (a.total || 1)) || a.name.localeCompare(b.name);
}

export function summarise(rows: TrainingRow[]): TrainingSummary {
  const topicsDone = rows.reduce((n, r) => n + r.done, 0);
  const topicsTarget = rows.reduce((n, r) => n + r.total, 0);

  // A person is counted under EVERY role they hold, so these people-counts sum
  // to more than the headcount. Every rendering of this table says so — the
  // alternative (picking one "primary" role) would need a ranking nobody has
  // agreed, and would quietly hide the second role's gap.
  const roles = new Map<string, TrainingRow[]>();
  for (const r of rows) {
    for (const role of r.roles.length ? r.roles : ['(no role)']) {
      const bucket = roles.get(role) ?? [];
      bucket.push(r);
      roles.set(role, bucket);
    }
  }

  return {
    people: rows.length,
    complete: rows.filter((r) => r.state === 'complete').length,
    started: rows.filter((r) => r.state === 'started').length,
    notStarted: rows.filter((r) => r.state === 'not-started').length,
    topicsDone,
    topicsTarget,
    pct: topicsTarget > 0 ? Math.round((topicsDone / topicsTarget) * 100) : 0,
    byRole: [...roles.entries()].map(([role, members]) => {
      const d = members.reduce((n, r) => n + r.done, 0);
      const t = members.reduce((n, r) => n + r.total, 0);
      return {
        role,
        people: members.length,
        complete: members.filter((r) => r.state === 'complete').length,
        notStarted: members.filter((r) => r.state === 'not-started').length,
        pct: t > 0 ? Math.round((d / t) * 100) : 0,
      };
    }).sort((a, b) => b.people - a.people || a.role.localeCompare(b.role)),
  };
}

// ─── Shared wording ───────────────────────────────────────────────────────────

const STATE_LABEL: Record<TrainingState, string> = {
  complete: 'Complete',
  started: 'In progress',
  'not-started': 'Nothing ticked',
};

/**
 * Says what the "nothing ticked" count actually measures.
 *
 * ⚠️ THE CAVEAT MUST ONLY NAME THINGS THE DOCUMENT ACTUALLY CONTAINS. This has
 * now been wrong twice: once pointing team readers at manager-only columns, and
 * once pointing Word readers at a Notes column that only the workbook has (the
 * document keeps its notes in an appendix, which is itself absent when nobody
 * has written any). So the manager version takes its pointer as an argument —
 * each caller passes what it actually rendered — rather than hard-coding a
 * layout that the next edit can invalidate silently.
 */
const ticksNoteManager = (where: string) =>
  'Counted on ticks. Someone who has read the guide but not ticked anything shows as nothing '
  + `ticked — check ${where} before assuming they have not looked.`;

const TICKS_NOTE_TEAM =
  'Counted on ticks, not on reading. Anyone who has worked through the guide without ticking the '
  // "may be lower", not "is lower": when nobody has read without ticking the
  // two counts are equal, and this sentence goes to the whole company.
  + 'boxes still counts under "nothing ticked", so treat that number as the most it could be — the '
  + 'true count of people who have not looked may be lower. Ticking as you go is what keeps it honest.';

/** Printed in every export — the file outlives the screen that explained it. */
const DENOMINATOR_NOTE =
  'The total differs from person to person on purpose. It is the foundations everyone reads plus '
  + 'the one track that matches their role, filtered to what they can actually reach in the Project '
  + 'Tracker — so a field leader has far fewer topics than a project manager, and neither is behind '
  + 'for it. Reading another track is welcome but is not counted, and neither is the screen-by-screen '
  + 'appendix, which is reference to look things up in rather than a course to finish. Compare the '
  + 'percentage, not the raw count.';

/**
 * Everyone is to have finished the guide by this date (product owner, 2026-08-11).
 *
 * One constant, read by the page and all four exports — a deadline typed twice
 * is a deadline that will eventually disagree with itself in the one document
 * people were told to trust.
 */
export const DEADLINE = '2026-08-28';

/** "Friday, August 28, 2026" — the weekday is worth the width on a deadline. */
export const deadlineLabel = () =>
  parseLocalDate(DEADLINE)?.toLocaleDateString('en-US',
    { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) ?? DEADLINE;

const DEADLINE_SENTENCE = `Everyone is to have worked through the guide by ${deadlineLabel()}.`;

/**
 * The reminder line FOR THE LIVE PAGE. Counts the days left, which is only true
 * at the moment it renders — the page re-renders, so that is fine here.
 *
 * ⚠️ NOT FOR A FILE. See `deadlineSentenceStatic`.
 */
export function deadlineSentence(): string {
  const days = daysFromToday(DEADLINE);
  const when = days == null ? ''
    : days > 1 ? ` That is ${days} days from today.`
    : days === 1 ? ' That is tomorrow.'
    : days === 0 ? ' That is today.'
    : ' That date has passed.';
  return `${DEADLINE_SENTENCE}${when}`;
}

/**
 * The reminder line FOR AN EXPORTED FILE.
 *
 * ⚠️ NO COUNTDOWN. A document is read days after it is written, and "that is 17
 * days from today" freezes into the file and goes quietly false — the reader
 * has no way to tell. Only the absolute date survives being forwarded.
 *
 * "Has passed" is the one relative clause that is safe to bake in: a deadline
 * already behind us at generation time is still behind us at read time. The
 * cover prints the generation date, so a reader can always place it.
 */
export function deadlineSentenceStatic(): string {
  const days = daysFromToday(DEADLINE);
  return days != null && days < 0 ? `${DEADLINE_SENTENCE} That date has passed.` : DEADLINE_SENTENCE;
}

const REVISION_NOTE =
  `Guide revision ${TRAINING.revision}, reviewed ${TRAINING.revised}. A tick is never removed `
  + 'automatically — when the guide changes, earlier ticks are flagged as an older revision rather '
  + 'than cleared.';

/**
 * Today, as the EXPORTER'S calendar date — the stamp on the cover and in the
 * filename.
 *
 * ⚠️ Not `toDateInputValue(new Date().toISOString())`. That is the UTC date, so
 * anyone east-of-UTC-midnight — an Eastern user exporting after 8pm — stamps
 * every file with TOMORROW and files two reports that appear to be from
 * different days. `todayInputValue` is built from local components for exactly
 * this reason, and says so in its own docblock.
 */
const stamp = () => todayInputValue();

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Revoked on the next tick rather than immediately: Safari aborts the download
  // if the object URL disappears in the same frame as the click.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// ─── Excel ────────────────────────────────────────────────────────────────────

/** Size columns to their widest cell so nothing lands as "####" or a truncated name. */
function fitColumns(rows: (string | number)[][]): XLSX.ColInfo[] {
  const widths: number[] = [];
  for (const row of rows) {
    row.forEach((cell, i) => {
      widths[i] = Math.max(widths[i] ?? 10, Math.min(String(cell ?? '').length + 2, 60));
    });
  }
  return widths.map((wch) => ({ wch }));
}

function sheet(aoa: (string | number)[][], opts: { filter?: boolean } = {}): XLSX.WorkSheet {
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = fitColumns(aoa);
  // Autofilter only on sheets whose first row really is a header — the summary
  // sheet is label/value pairs and a filter on it would be nonsense.
  if (opts.filter && aoa.length > 1) {
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: aoa.length - 1, c: (aoa[0]?.length ?? 1) - 1 } }),
    };
  }
  return ws;
}

/** Manager workbook — names, per-person numbers, and every note. */
export function exportManagerXlsx(rows: TrainingRow[]) {
  const s = summarise(rows);

  const roster: (string | number)[][] = [
    ['Person', 'Email', 'Roles', 'Track counted', 'Status', 'Completed', 'Target', 'Percent complete', 'Older revision', 'Notes', 'Last activity'],
    ...rows.map((r) => [
      r.name, r.email ?? '', r.rolesText, r.trackLabel, STATE_LABEL[r.state],
      r.done, r.total, r.pct, r.stale, r.notes.length,
      r.last ? fmtInstantDate(r.last) : 'Never',
    ]),
  ];

  const summary: (string | number)[][] = [
    ['Project Tracker training — progress report', ''],
    ['Generated', fmtDate(stamp())],
    ['Deadline', deadlineLabel()],
    ['Guide revision', `${TRAINING.revision} (reviewed ${TRAINING.revised})`],
    ['', ''],
    ['People', s.people],
    ['Complete', s.complete],
    ['In progress', s.started],
    ['Nothing ticked yet', s.notStarted],
    ['Topics ticked', s.topicsDone],
    ['Topics targeted', s.topicsTarget],
    ['Company-wide complete', `${s.pct}%`],
    ['', ''],
    ['By role', ''],
    ['Role', 'People', 'Complete', 'Nothing ticked', 'Percent complete'],
    ...s.byRole.map((r) => [r.role, r.people, r.complete, r.notStarted, r.pct]),
    ['', ''],
    ['Note', DENOMINATOR_NOTE],
    // The workbook really does carry both — a Notes count on "By person" and a
    // full Notes sheet.
    ['Note', ticksNoteManager('the Notes and Last activity columns')],
    ['Note', 'A person holding two roles is counted under both, so the role people-counts sum to more than the headcount.'],
    ['Note', REVISION_NOTE],
  ];

  const notes: (string | number)[][] = [
    ['Person', 'Topic', 'Ticked', 'Note'],
    ...rows.flatMap((r) => r.notes.map((n) => [r.name, n.title, n.completedAt ? 'Yes' : 'No', n.note])),
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet(summary), 'Summary');
  XLSX.utils.book_append_sheet(wb, sheet(roster, { filter: true }), 'By person');
  XLSX.utils.book_append_sheet(wb, sheet(notes, { filter: true }), 'Notes');
  XLSX.writeFile(wb, `training-progress-managers-${stamp()}.xlsx`);
}

/** Team workbook — counts only. Builds no name column at all. */
export function exportSummaryXlsx(rows: TrainingRow[]) {
  const s = summarise(rows);

  const summary: (string | number)[][] = [
    ['Project Tracker training — where we stand', ''],
    ['Generated', fmtDate(stamp())],
    ['Deadline', deadlineLabel()],
    ['Guide revision', `${TRAINING.revision} (reviewed ${TRAINING.revised})`],
    ['', ''],
    ['People with access', s.people],
    ['Finished the guide', s.complete],
    ['Part way through', s.started],
    ['Nothing ticked yet', s.notStarted],
    ['Company-wide complete', `${s.pct}%`],
    ['', ''],
    ['Role', 'People', 'Finished', 'Nothing ticked', 'Percent complete'],
    ...s.byRole.map((r) => [r.role, r.people, r.complete, r.notStarted, r.pct]),
    ['', ''],
    ['Note', DENOMINATOR_NOTE],
    ['Note', TICKS_NOTE_TEAM],
    ['Note', 'A person holding two roles is counted under both, so the role people-counts sum to more than the headcount.'],
    ['Note', REVISION_NOTE],
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet(summary), 'Where we stand');
  XLSX.writeFile(wb, `training-progress-summary-${stamp()}.xlsx`);
}

// ─── Word ─────────────────────────────────────────────────────────────────────

const GREY = '767676';

const para = (text: string, opts: { size?: number; color?: string; italics?: boolean; after?: number; bold?: boolean } = {}) =>
  new Paragraph({
    children: [new TextRun({
      text, size: opts.size ?? 21, color: opts.color, italics: opts.italics, bold: opts.bold,
    })],
    spacing: { after: opts.after ?? 120 },
  });

function docTable(headers: string[], body: string[][], widths?: number[]): Table {
  const cell = (text: string, i: number, opts: { bold?: boolean; fill?: string } = {}) => new TableCell({
    shading: opts.fill ? { fill: opts.fill } : undefined,
    width: widths ? { size: widths[i], type: WidthType.PERCENTAGE } : undefined,
    children: [new Paragraph({ children: [new TextRun({ text, bold: opts.bold, size: 18 })] })],
  });
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({
        tableHeader: true,
        children: headers.map((h, i) => cell(h, i, { bold: true, fill: 'F5F5F4' })),
      }),
      ...body.map((r) => new TableRow({ children: r.map((c, i) => cell(c, i)) })),
    ],
  });
}

/** Cover block shared by both documents. */
function cover(title: string, subtitle: string): (Paragraph | Table)[] {
  return [
    new Paragraph({ text: title, heading: HeadingLevel.TITLE, spacing: { after: 120 } }),
    new Paragraph({
      children: [new TextRun({
        text: `Generated ${fmtDate(stamp())}  ·  guide revision ${TRAINING.revision}, reviewed ${TRAINING.revised}`,
        size: 21, color: GREY,
      })],
      spacing: { after: 60 },
    }),
    // The deadline sits above the fold in both documents — it is the reason
    // either of them gets sent.
    new Paragraph({
      children: [new TextRun({ text: deadlineSentenceStatic(), size: 22, bold: true, color: '363636' })],
      spacing: { before: 120, after: 120 },
    }),
    new Paragraph({
      children: [new TextRun({ text: subtitle, size: 19, italics: true, color: GREY })],
      spacing: { after: 360 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: 'DDDDDD', space: 8 } },
    }),
  ];
}

function headlineTable(s: TrainingSummary, labels: [string, string, string, string]): Table {
  return docTable(
    ['', 'People'],
    [
      [labels[0], String(s.people)],
      [labels[1], String(s.complete)],
      [labels[2], String(s.started)],
      [labels[3], String(s.notStarted)],
      ['Topics ticked company-wide', `${s.topicsDone} of ${s.topicsTarget}  ·  ${s.pct}%`],
    ],
    [60, 40],
  );
}

function wrap(title: string, children: (Paragraph | Table)[]): Document {
  return new Document({
    creator: 'East & Westbrook — Project Tracker',
    title,
    description: `Revision ${TRAINING.revision}, reviewed ${TRAINING.revised}`,
    styles: {
      default: {
        document: { run: { font: 'Arial', size: 21 } },
        title: { run: { font: 'Arial', size: 44, bold: true, color: '363636' } },
        heading1: { run: { font: 'Arial', size: 28, bold: true, color: '363636' }, paragraph: { spacing: { before: 360, after: 120 } } },
      },
    },
    sections: [{
      headers: {
        default: new Header({
          children: [new Paragraph({
            alignment: AlignmentType.RIGHT,
            children: [new TextRun({ text: `${title} — rev ${TRAINING.revision}`, size: 16, color: '999999' })],
          })],
        }),
      },
      footers: {
        default: new Footer({
          children: [new Paragraph({
            alignment: AlignmentType.RIGHT,
            children: [new TextRun({ children: ['Page ', PageNumber.CURRENT, ' of ', PageNumber.TOTAL_PAGES], size: 16, color: '999999' })],
          })],
        }),
      },
      children,
    }],
  });
}

/** Manager document — names, the not-started callout, the roster and every note. */
export async function exportManagerDocx(rows: TrainingRow[]) {
  const s = summarise(rows);
  const title = 'Project Tracker training — progress report';

  const children: (Paragraph | Table)[] = [
    ...cover(title, 'For managers. This copy names individuals and lists their notes — it is not the version to send company-wide.'),
    para(DENOMINATOR_NOTE, { color: '5A5A56' }),
    headlineTable(s, ['People with access', 'Finished the guide', 'Part way through', 'Nothing ticked yet']),
  ];

  // Hoisted: the caveat below names where notes live, and in this document that
  // depends on whether the appendix gets rendered at all.
  const withNotes = rows.filter((r) => r.notes.length > 0);
  const notesLiveIn = withNotes.length
    ? 'the notes appendix at the end and the Last activity column'
    : 'the Last activity column';

  const notStarted = rows.filter((r) => r.state === 'not-started');
  children.push(new Paragraph({ text: 'Nothing ticked yet', heading: HeadingLevel.HEADING_1 }));
  if (notStarted.length === 0) {
    children.push(para('Everyone with access has ticked at least one topic.'));
  } else {
    children.push(para(`${notStarted.length} of ${s.people} have not ticked a single topic.`, { color: '5A5A56' }));
    children.push(para(ticksNoteManager(notesLiveIn), { size: 19, color: GREY }));
    children.push(docTable(
      ['Person', 'Roles', 'Track', 'Topics to read'],
      notStarted.map((r) => [r.name, r.rolesText, r.trackLabel, String(r.total)]),
      [32, 28, 25, 15],
    ));
  }

  children.push(new Paragraph({ text: 'Everyone', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }));
  children.push(docTable(
    ['Person', 'Roles', 'Track', 'Completed', '%', 'Older rev.', 'Last activity'],
    rows.map((r) => [
      r.name, r.rolesText, r.trackLabel, `${r.done} of ${r.total}`, `${r.pct}%`,
      r.stale > 0 ? String(r.stale) : '—',
      r.last ? fmtInstantDate(r.last) : 'Never',
    ]),
    [22, 20, 20, 12, 8, 8, 10],
  ));

  children.push(new Paragraph({ text: 'By role', heading: HeadingLevel.HEADING_1 }));
  children.push(para('A person holding two roles is counted under both, so these people-counts sum to more than the headcount.', { size: 19, color: GREY }));
  children.push(docTable(
    ['Role', 'People', 'Finished', 'Nothing ticked', '% complete'],
    s.byRole.map((r) => [r.role, String(r.people), String(r.complete), String(r.notStarted), `${r.pct}%`]),
    [40, 15, 15, 15, 15],
  ));

  if (withNotes.length) {
    children.push(new Paragraph({ text: 'Notes people have written', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }));
    children.push(para('Written against a topic while reading. Everyone is told, on the page, that these are visible to managers.', { size: 19, color: GREY }));
    for (const r of withNotes) {
      children.push(new Paragraph({
        children: [new TextRun({ text: r.name, bold: true, size: 23 })],
        spacing: { before: 240, after: 60 }, keepNext: true,
      }));
      for (const n of r.notes) {
        children.push(new Paragraph({
          children: [
            new TextRun({ text: n.title, bold: true, size: 19 }),
            ...(n.completedAt ? [] : [new TextRun({ text: '  ·  not ticked', size: 17, color: GREY })]),
          ],
          spacing: { before: 100, after: 30 }, indent: { left: 340 }, keepNext: true,
        }));
        children.push(new Paragraph({
          children: [new TextRun({ text: n.note, size: 19, color: '5A5A56' })],
          spacing: { after: 60 }, indent: { left: 340 },
        }));
      }
    }
  }

  children.push(para(REVISION_NOTE, { size: 18, color: GREY, italics: true, after: 0 }));

  triggerDownload(await Packer.toBlob(wrap(title, children)), `Training progress — managers (${stamp()}).docx`);
}

/** Team document — counts and role tallies. Names nobody. */
export async function exportSummaryDocx(rows: TrainingRow[]) {
  const s = summarise(rows);
  const title = 'Project Tracker training — where we stand';

  const children: (Paragraph | Table)[] = [
    ...cover(title, 'A company-wide snapshot. No individual is named.'),
    para(DENOMINATOR_NOTE, { color: '5A5A56' }),
    headlineTable(s, ['People with access', 'Finished the guide', 'Part way through', 'Nothing ticked yet']),
    para(TICKS_NOTE_TEAM, { size: 19, color: GREY }),
    new Paragraph({ text: 'By role', heading: HeadingLevel.HEADING_1 }),
    para('A person holding two roles is counted under both, so these people-counts sum to more than the headcount.', { size: 19, color: GREY }),
    docTable(
      ['Role', 'People', 'Finished', 'Nothing ticked', '% complete'],
      s.byRole.map((r) => [r.role, String(r.people), String(r.complete), String(r.notStarted), `${r.pct}%`]),
      [40, 15, 15, 15, 15],
    ),
    new Paragraph({ text: 'Where to find the guide', heading: HeadingLevel.HEADING_1 }),
    // Matches the real route: the header "?" opens HelpDrawer, which links on
    // to /training. Naming a second entry point would send people looking for
    // one that does not exist.
    para('Open the Project Tracker, click the "?" button in the header, then "Full training guide". Your progress saves as you tick topics, so you can pick up where you left off.'),
    para(REVISION_NOTE, { size: 18, color: GREY, italics: true, after: 0 }),
  ];

  triggerDownload(await Packer.toBlob(wrap(title, children)), `Training progress — summary (${stamp()}).docx`);
}
